<template>
  <fieldset class="contact-phone"
            :class="{ 'app-embedded-fieldset': embedded }"><!-- using fieldset as verification code was expected in future release -->
    <legend>
      <span>Phone<span>(xxx-xxx-xxxx)</span></span>
    </legend>
    <span class="app-contact-inputs">
      <ValidationForm ref="phoneForm">
        <div class="app-input-group app-contact-info" :class="{ 'app-invalid-group': invalidPhone || invalidPhoneNumChars }">
          <div class="app-phone-number">
            <Field name="myPhoneAC" rules="numeric|length:3" >
              <label for="userPhoneAC" class="sr-only">Area code</label>
              <input id="userPhoneAC" type="tel" ref="acFieldRef" v-model="myPhoneAC" @keyup="switchFocus"
              :readonly="editable" :disabled="editable">
            </Field> -
            <Field name="myPhoneF3" rules="numeric|length:3" >
              <label for="userPhoneF3" class="sr-only">First three digits</label>
              <input id="userPhoneF3" type="tel" ref="f3FieldRef" v-model="myPhoneF3" @keyup="switchFocus"
              :readonly="editable" :disabled="editable">
            </Field> -
            <Field name="myPhoneL4" rules="numeric|length:4">
              <label for="userPhoneL4" class="sr-only">Last four digits</label>
              <input id="userPhoneL4" type="tel" ref="l4FieldRef" v-model="myPhoneL4" @keyup="switchFocus"
              :readonly="editable" :disabled="editable">
              <p v-if="invalidPhoneNumChars">Phone number contains invalid characters.</p>
              <p v-else-if="invalidPhone">Please enter a valid phone number.</p>
            </Field>
          </div>
        </div>
      </ValidationForm>
    </span>
  </fieldset>
</template>


<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Form as ValidationForm, Field, defineRule, useForm } from 'vee-validate';
import {  numeric } from '@vee-validate/rules';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter'; 

defineRule('length', (value, [limit]) => {
  if (!value || !value.length) {
    return true; // Skip validation if the field is empty (unless 'required' is also used)
  }
  if (value.length !== limit) {
    return `This field must have ${limit} digits.`;
  }
  return true;
});

defineRule('numeric', numeric); 

const props = defineProps({
  embedded: {
    type: Boolean,
    required: false,
    default: false,
  },
  isModal: {
    type: Boolean,
    required: false,
    default: false,
  },
  verify: {
    type: Boolean,
    required: false,
    default: false,
  },
  editable: {
    type: Boolean,
    required: false,
    default: false,
  }
});

const emit = defineEmits(['doResetVerify', 'doPhone']);

// Phone formatter composable
const { formatPhoneForWebapp } = usePhoneFormatter();

// Reactive State
const store = useStore();
const myPhoneAC = ref('');
const myPhoneF3 = ref('');
const myPhoneL4 = ref('');
const myPhone = ref('');
const invalidPhone = ref(false);
const phoneForm = ref(null);

// Template refs
const acFieldRef = ref(null);
const f3FieldRef = ref(null);
const l4FieldRef = ref(null);


const { validate } = useForm();

// Computed properties
const invalidPhoneNumChars = computed(() => {
  const phoneNum = myPhoneAC.value + myPhoneF3.value + myPhoneL4.value;
  if (phoneNum.length === 0) {
    return false;
  }
  const pattern = /^\d+$/;
  return !pattern.test(phoneNum);
});

const resDetails = computed(() => store.state.user.resDetails);
const customerDetail = computed(() => store.state.user.customerDetail);

// Watchers
watch(myPhoneAC, () => {
  updateMyPhone('myPhoneAC');
});

watch(myPhoneF3, () => {
  updateMyPhone('myPhoneF3');
});

watch(myPhoneL4, () => {
  updateMyPhone('myPhoneL4');
});
/**
 * SPECIAL handling of modal phone check.
 * !!! verify prop value is NOT consistently being passed to child components from within a modal !!!
 * !!! Vue $emit (to component parent) is not aware of child's await Promise call so they are cross-wired !!!
 * Must do extra checks since we cannot depend on parent ModalContact's bound variable to tell us when user is done.
 */
watch(myPhone, async (newVal) => {
  // for both Step 3 (after updated to type=tel) and Step 5 modal
  // len checks to prevent instant validation as soon as user has keyed
  // myPhone will be '' if there are invalid chars
  if (newVal.length === 0 || newVal.length === 10) {
    await checkPhone();
  }
});
/**
 * For Step 3 (non-modal) only. Step 5 modal depends on myPhone watcher.
 * Modal props do not behave like regular components (Step 3) where a child does not always detect
 * its parent's bound variable when it's changed.
 */
watch(() => props.verify, async (newVal) => {
  // MUST depend on parent to signal user's intention to move on in Step 3
  // !!! See comment in myPhone watcher !!!
  if (newVal) {
    await checkPhone();
  }
});

// Lifecycle Hooks
onMounted(() => {
// pre-fill if info is available

  if (resDetails.value && resDetails.value.userInfo) {
    const info = resDetails.value.userInfo.reservationSendConfirmationRequest;
    if (info && info.phone && info.phone.length > 0) {
      const phoneNum = formatPhoneForWebapp(info.phone);
      if (phoneNum.length === 10) {
        myPhone.value = phoneNum;
        split(phoneNum);
      }
    }
  }
  if (customerDetail.value) {
    const info = customerDetail.value;
    if (info && info.phone && info.phone.length > 0) {
      const phoneNum = formatPhoneForWebapp(info.phone);
      if (phoneNum.length === 10) {
        myPhone.value = phoneNum;
        split(phoneNum);
      }
    }
  }
});

//Methods
const checkPhone = async () => {
  if(invalidPhoneNumChars.value) {
    return; 
  }
  invalidPhone.value = !await hasValidPhone();
      

  if (invalidPhone.value) {
    emit('doResetVerify');
  }

  // pass info back to parent (UserContact or ModalContact)
  // this.$emit('doPhone', { phone: this.myPhone, isValid: !this.invalidPhone });
  emit('doPhone', { phone: myPhone.value, isValid: !invalidPhone.value });
}

/**
 * Applicable for both Step 3 and Step 5 modal.
 * @returns {Promise<boolean>}
 */
const hasValidPhone = async () => {
  if (invalidPhoneNumChars.value) { // Access computed ref with .value
    return false;
  }

  // Use the validate function from useForm to trigger validation
  // This will validate all fields associated with this form context
  const { valid: formValid } = await validate(); 


  return (myPhone.value.length === 0 || (formValid && myPhone.value.length === 10));
};

const split = (num) => {
  if (!num || num.length < 10) {
    myPhoneAC.value = '';
    myPhoneF3.value = '';
    myPhoneL4.value = '';
    return;
  }
  // split 10-digit phone number to 3 UI parts
  myPhoneAC.value = num.substring(0, 3);
  myPhoneF3.value = num.substring(3, 6);
  myPhoneL4.value = num.substring(6, 10);
};

const stitch = ()=> {
  if (invalidPhoneNumChars.value) {
    myPhone.value = '';
  } else {
    myPhone.value = `${myPhoneAC.value}${myPhoneF3.value}${myPhoneL4.value}`;
  }
}

const switchFocus = (e) => {
  const isTab = e.key === 'Tab';
  const isShift = e.key === 'Shift';

  if (!isTab && !isShift) {
    const targetId = e.target.id;
    const targetLength = e.target.value.length;

    switch (targetId) {
      case 'userPhoneAC':
        if (targetLength === 3) {
          nextTick(() => {
            // Use the ref on the Field component to focus the internal input
            f3FieldRef.value?.focus();
          });
        }
        break;
      case 'userPhoneF3':
        if (targetLength === 3) {
          nextTick(() => {
            l4FieldRef.value?.focus();
          });
        }
        break;
    }
  }
};

/**
 * Stop user from entering more digits than allowed. This is necessary when using input type=numbers where
 * there is no native HTML maxlength validation (maxlength is only available for type=text).
 * @param data
 * @returns {*|boolean|string}
 */
const truncateMax = (data) => {
  const partial = data.partial;
  const max = data.max;
  if (partial.length > max) {
    return partial.substring(0, max);
  }
  return partial;
};

const updateMyPhone = (field) => {
  switch (field) {
    case 'myPhoneL4': // go first since the other two fields will normally auto-advance based on length
      myPhoneL4.value = truncateMax({ partial: myPhoneL4.value, max: 4, min: 4 });
      break;
    case 'myPhoneF3':
      myPhoneF3.value = truncateMax({ partial: myPhoneF3.value, max: 3, min: 3 });
      break;
    case 'myPhoneAC':
      myPhoneAC.value = truncateMax({ partial: myPhoneAC.value, max: 3, min: 3 });
      break;
    default:
      // nada
  }
  // construct myPhone
  stitch();
}
defineExpose({
  checkPhone,
  hasValidPhone,
  invalidPhone,
  split
});
</script>


<!-- <script>
import { mapState } from 'vuex'
import { phoneMixin } from '@/utils/phoneMixin'
import { setInteractionMode , ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { length, numeric } from 'vee-validate/dist/rules'

extend('length', {
  ...length,
  message: (field, params) => {
    return `${params._field_} must have 10 digits.`;
  }
});
extend('numeric', numeric);

export default {
  name: 'ContactPhone',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    embedded: {             // embedded fieldset (in Step 3)?
      type: Boolean,
      required: false,
      default: false,
    },
    isModal: {              // SPECIAL: see comment in myPhone watcher
      type: Boolean,
      required: false,
      default: false,
    },
    verify: {
      type: Boolean,
      required: false,
      default: false,
    },
    editable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  mixins: [
    phoneMixin,
  ],
  computed: {
    ...mapState([
      'reservationSlotId',
      'resDetails',
      'customerDetail'
    ]),
    invalidPhoneNumChars() {
      const phoneNum = this.myPhoneAC + this.myPhoneF3 + this.myPhoneL4;
      if (phoneNum.length === 0) {
        return false;
      }
      const pattern = /^\d+$/;
      return !pattern.test(phoneNum);
    },
  },
  data() {
    return {
      myPhoneAC: '',
      myPhoneF3: '',
      myPhoneL4: '',
      myPhone: '',
      invalidPhone: false,
      isShiftTab: false,
    };
  },
  watch: {
    myPhoneAC() {
      this.updateMyPhone('myPhoneAC');
    },
    myPhoneF3() {
      this.updateMyPhone('myPhoneF3');
    },
    myPhoneL4() {
      this.updateMyPhone('myPhoneL4');
    },
    /**
     * SPECIAL handling of modal phone check.
     * !!! verify prop value is NOT consistently being passed to child components from within a modal !!!
     * !!! Vue $emit (to component parent) is not aware of child's await Promise call so they are cross-wired !!!
     * Must do extra checks since we cannot depend on parent ModalContact's bound variable to tell us when user is done.
     */
    myPhone() {
      // for both Step 3 (after updated to type=tel) and Step 5 modal
      // len checks to prevent instant validation as soon as user has keyed
      // myPhone will be '' if there are invalid chars
      if (this.myPhone.length === 0 || this.myPhone.length === 10) {
        this.checkPhone();
      }
    },
    /**
     * For Step 3 (non-modal) only. Step 5 modal depends on myPhone watcher.
     * Modal props do not behave like regular components (Step 3) where a child does not always detect
     * its parent's bound variable when it's changed.
     */
    async verify() {
      // MUST depend on parent to signal user's intention to move on in Step 3
      // !!! See comment in myPhone watcher !!!
      if (this.verify) {
        await this.checkPhone();
      }
    },
  },
  created() {
    setInteractionMode('aggressive');
  },
  mounted() {
    // pre-fill if info is available
    if (this.resDetails && this.resDetails.userInfo) {
      const info = this.resDetails.userInfo.reservationSendConfirmationRequest;
      if (info && info.phone && info.phone.length > 0) {
        const phoneNum = this.formatPhoneForWebapp(info.phone);
        if (phoneNum.length === 10) {
          this.myPhone = phoneNum;
          this.split(phoneNum);
        }
      }
    }


    if ( this.customerDetail) {
      const info = this.customerDetail;
      if (info && info.phone && info.phone.length > 0) {
        const phoneNum = this.formatPhoneForWebapp(info.phone);
        if (phoneNum.length === 10) {
          this.myPhone = phoneNum;
          this.split(phoneNum);
        }
      }
    }

    
  },
  methods: {
    async checkPhone() {
      this.invalidPhone = !await this.hasValidPhone();

      if (this.invalidPhone) {
        this.$emit('doResetVerify')
      }

      // pass info back to parent (UserContact or ModalContact)
      this.$emit('doPhone', { phone: this.myPhone, isValid: !this.invalidPhone });
    },
    /**
     * Applicable for both Step 3 and Step 5 modal.
     * @returns {Promise<boolean>}
     */
    async hasValidPhone() {
      if (this.invalidPhoneNumChars) {
        return false;
      }

      const phoneNumValid = await this.$refs.phoneNumObserver.validate();
      return (this.myPhone.length === 0 || (phoneNumValid && this.myPhone.length === 10));
    },
    split(num) {
      // split 10-digit phone number to 3 UI parts
      this.myPhoneAC = num.substr(0, 3);
      this.myPhoneF3 = num.substr(3, 3);
      this.myPhoneL4 = num.substr(6, 4);
    },
    async stitch() {
      if (this.invalidPhoneNumChars) {
        this.myPhone = '';
      } else {
        this.myPhone = `${this.myPhoneAC}${this.myPhoneF3}${this.myPhoneL4}`;
      }
    },
    switchFocus(e) {
      const isTab = e.key === 'Tab' || e.keyCode === 9;
      const isShift = e.key === 'Shift' || e.keyCode === 16;

      if (!isTab && !isShift) {
        const ref = e.target.id;
        switch (ref) {
          case 'userPhoneAC':
            if (this.myPhoneAC.length === 3) {
              this.$refs.userPhoneF3.focus();
            }
            break;
          case 'userPhoneF3':
            if (this.myPhoneF3.length === 3) {
              this.$refs.userPhoneL4.focus();
            }
            break;
          case 'userPhoneL4':
          default:
            // userPhoneL4 case no longer applicable in refactored component
        }
      }
    },
    /**
     * Stop user from entering more digits than allowed. This is necessary when using input type=numbers where
     * there is no native HTML maxlength validation (maxlength is only available for type=text).
     * @param data
     * @returns {*|boolean|string}
     */
    truncateMax(data) {
      const partial = data.partial;
      const max = data.max;
      if (partial.length > max) {
        return partial.substr(0, max);
      }
      return partial;
    },
    updateMyPhone(field) {
      switch (field) {
        case 'myPhoneL4':   // go first since the other two fields will normally auto-advance based on length
          this.myPhoneL4 = this.truncateMax({ partial: this.myPhoneL4, max: 4 });
          break;
        case 'myPhoneF3':
          this.myPhoneF3 = this.truncateMax({ partial: this.myPhoneF3, max: 3 });
          break;
        case 'myPhoneAC':
          this.myPhoneAC = this.truncateMax({ partial: this.myPhoneAC, max: 3 });
          break;
        default:
          // nada
      }

      // construct myPhone
      this.stitch();
    }
  }
}
</script> -->

<style lang="scss" scoped>
fieldset.app-embedded-fieldset {
  legend {
    font-size: 1rem;
    padding: 0.5em 0;
  }
}
.app-contact-inputs {
  --box-width: 320px;
  --box-width-tiny: calc(var(--box-width) * 0.85);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  label {
    display: block;
  }
  .app-contact-info {
    width: max-content;
    .app-phone-number {
      input {
        width: 55px;
        display: inline-block;
        padding: 0.5em;
        text-align: center;
      }
      input:last-child {
        width: 65px;
      }
    }
    .app-user-email {
      min-width: var(--box-width);
      ~ p {
        width: var(--box-width);
      }
      @media (max-width: 425px) {
        min-width: var(--box-width-tiny);
        ~ p {
          width: var(--box-width-tiny);
        }
      }
    }
  }
  .app-verification-info {
    .app-verification-number {
      display: inline-block;
      input {
        display: inline-block;
        margin-right: 0.5em;
        padding: 0.25em;
        text-align: center;
        width: 40px;
      }
    }
  }
}
</style>
